import React, { useState, useMemo, useCallback } from "react";
import { TextInput, Text, Group, Box, Stack, ScrollArea, UnstyledButton, Divider, Avatar } from "@mantine/core";
import { StackedModal } from "@em/ui";
import { useTranslation } from "react-i18next";
import Fuse, { type IFuseOptions } from "fuse.js";

import { User } from "@/utils/api";
import { AppButton, AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

interface UserOptionItem {
  id: string;
  label: string;
  details?: string;
  avatarUrl?: string;
  initials: string;
  isAllOption?: boolean;
  user?: User;
}

export interface UserPickerModalProps {
  opened: boolean;
  onClose: () => void;

  /** Currently selected user ID (null = "All Users" if showAllOption) */
  value: string | null | undefined;

  /** Callback when a user is selected */
  onSelect: (userId: string | null, user?: User) => void;

  /** Available users to display */
  users: User[];

  /** Whether to show "All Users" option */
  showAllOption?: boolean;

  /** Label for "All Users" option */
  allOptionLabel?: string;

  /** Whether to show user details (email) in dropdown */
  showDetails?: boolean;
}

// ============================================
// Constants
// ============================================

const ALL_USERS_VALUE = "__all__";

const FUSE_OPTIONS: IFuseOptions<UserOptionItem> = {
  keys: [
    { name: "label", weight: 0.7 },
    { name: "details", weight: 0.3 },
  ],
  threshold: 0.35,
  distance: 100,
  minMatchCharLength: 2,
  ignoreLocation: true,
};

// ============================================
// Helper Functions
// ============================================

const getUserDisplayName = (user: User): string => {
  const nameParts = [user.firstName, user.middleName, user.lastName].filter(
    Boolean
  );

  if (nameParts.length > 0) {
    return nameParts.join(" ");
  }

  return user.username || user.email || "Unknown User";
};

const getUserInitials = (user: User): string => {
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  if (user.firstName) {
    return user.firstName.substring(0, 2).toUpperCase();
  }
  if (user.username) {
    return user.username.substring(0, 2).toUpperCase();
  }
  if (user.email) {
    return user.email.substring(0, 2).toUpperCase();
  }
  return "??";
};

// ============================================
// Component
// ============================================

export const UserPickerModal: React.FC<UserPickerModalProps> = ({
  opened,
  onClose,
  value,
  onSelect,
  users,
  showAllOption = false,
  allOptionLabel,
  showDetails = true,
}) => {
  const { t } = useTranslation(["users", "common"]);
  const [searchValue, setSearchValue] = useState("");

  // Reset search when modal opens
  React.useEffect(() => {
    if (opened) {
      setSearchValue("");
    }
  }, [opened]);

  const defaultAllOptionLabel =
    allOptionLabel || t("users:selectors.user.allUsers", "All Users");

  // Build option items
  const allOptions = useMemo((): UserOptionItem[] => {
    const options: UserOptionItem[] = [];

    if (showAllOption) {
      options.push({
        id: ALL_USERS_VALUE,
        label: defaultAllOptionLabel,
        initials: "",
        isAllOption: true,
      });
    }

    users.forEach((user) => {
      if (user.id) {
        options.push({
          id: user.id,
          label: getUserDisplayName(user),
          details: showDetails ? user.email || undefined : undefined,
          avatarUrl: user.avatarUrl || undefined,
          initials: getUserInitials(user),
          user,
        });
      }
    });

    return options;
  }, [users, showAllOption, defaultAllOptionLabel, showDetails]);

  // Fuse index for fuzzy search
  const fuse = useMemo(() => new Fuse(allOptions, FUSE_OPTIONS), [allOptions]);

  // Filtered options
  const filteredOptions = useMemo(() => {
    const search = searchValue.trim();
    if (!search) return allOptions;
    return fuse.search(search).map((result) => result.item);
  }, [allOptions, fuse, searchValue]);

  // Handle selection
  const handleSelect = useCallback(
    (option: UserOptionItem) => {
      if (option.isAllOption) {
        onSelect(null, undefined);
      } else {
        onSelect(option.id, option.user);
      }
      onClose();
    },
    [onSelect, onClose]
  );

  // Determine selected value for checkmark
  const selectedId = useMemo(() => {
    if (value === null && showAllOption) return ALL_USERS_VALUE;
    return value ?? null;
  }, [value, showAllOption]);

  return (
    <StackedModal
      opened={opened}
      onClose={onClose}
      title={t("users:selectors.user.modalTitle", {
        defaultValue: "Select User",
      })}
      fullScreen
      zIndex={1000}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 60px)",
          padding: 0,
        },
      }}
    >
      {/* Search input */}
      <Box px="md" pt="xs" pb="sm" style={{ flexShrink: 0 }}>
        <TextInput
          placeholder={t("users:selectors.user.searchPlaceholder", {
            defaultValue: "Search users...",
          })}
          value={searchValue}
          onChange={(e) => setSearchValue(e.currentTarget.value)}
          leftSection={<AppIcon iconName="Search" size={18} />}
          rightSection={
            searchValue ? (
              <AppButton
                iconName="X"
                iconSize={16}
                onClick={() => setSearchValue("")}
              />
            ) : undefined
          }
          rightSectionPointerEvents="auto"
          size="md"
          autoFocus
        />
      </Box>

      <Divider />

      {/* User list */}
      <ScrollArea style={{ flex: 1 }} type="auto" offsetScrollbars>
        <Stack gap={0} px="md" py="xs">
          {filteredOptions.map((option) => {
            const isSelected = selectedId === option.id;

            return (
              <UnstyledButton
                key={option.id}
                onClick={() => handleSelect(option)}
                py="sm"
                px="xs"
                style={{ borderRadius: 8 }}
              >
                <Group gap="sm" wrap="nowrap" justify="space-between">
                  <Group
                    gap="sm"
                    wrap="nowrap"
                    align="flex-start"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Box pt={2}>
                      {option.isAllOption ? (
                        <AppIcon iconName="Users" size={24} />
                      ) : (
                        <Avatar
                          src={option.avatarUrl}
                          size={32}
                          radius="xl"
                          color="brand"
                        >
                          {option.initials}
                        </Avatar>
                      )}
                    </Box>
                    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        size="md"
                        fw={option.isAllOption ? 500 : undefined}
                        lineClamp={1}
                      >
                        {option.label}
                      </Text>
                      {option.details && (
                        <Text size="sm" c="dimmed" lineClamp={1}>
                          {option.details}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                  {isSelected && (
                    <AppIcon iconName="Check" size={18} color="blue" />
                  )}
                </Group>
              </UnstyledButton>
            );
          })}

          {/* No results */}
          {filteredOptions.length === 0 && searchValue.trim() && (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              {t("common:form.noResults", {
                defaultValue: "No users found",
              })}
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </StackedModal>
  );
};